import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth, useUser, SignOutButton } from '@clerk/clerk-react';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  User, 
  Settings, 
  LogOut,
  ChevronDown,
  FileText,
  Palette
} from 'lucide-react';
import { UsageStatusWidget } from '@/modules/app/components/usage';
import { CreditPurchaseModal } from '@/modules/app/components/usage';

const CleanModernNavbar: React.FC = () => {
  const { isSignedIn } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `text-sm px-3 py-1.5 rounded-md transition-colors ${
      isActive
        ? 'bg-muted text-foreground font-medium shadow-sm'
        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
    }`;

  return (
    <nav className="bg-background sticky top-0 z-50 border-b border-border">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        <div className="flex items-center space-x-6">
          <NavLink to="/app/dashboard" className="text-xl font-bold text-foreground">
            Keyword OS
          </NavLink>
          
          {/* Only show navigation links if user is signed in */}
          {isSignedIn && (
            <div className="flex items-center space-x-1">
              <NavLink to="/app/dashboard" className={navLinkClass}>
                Dashboard
              </NavLink>
              <NavLink to="/app/keyword-research" className={navLinkClass}>
                Keyword Research
              </NavLink>
              <NavLink to="/app/workspace" className={navLinkClass}>
                Workspaces
              </NavLink>
            </div>
          )}
        </div>
        
        {/* Usage Status Widget and User Profile */}
        <div className="flex items-center space-x-4">
          {/* Usage Status Widget - Only show if signed in and on app pages */}
          {isSignedIn && (
            <UsageStatusWidget 
              onPurchaseClick={() => setShowPurchaseModal(true)}
              className="hidden sm:flex"
            />
          )}
          
          {/* User Profile Dropdown - Only show if signed in */}
          {isSignedIn && user ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm"
                className="flex items-center space-x-2 h-9"
                aria-label={`Account menu for ${user.firstName || user.emailAddresses[0]?.emailAddress}`}
              >
                <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center">
                  {user.imageUrl ? (
                    <img 
                      src={user.imageUrl} 
                      alt="Profile" 
                      className="w-7 h-7 rounded-full"
                    />
                  ) : (
                    <User className="h-4 w-4 text-primary-foreground" />
                  )}
                </div>
                <span className="text-sm text-foreground hidden sm:inline">
                  {user.firstName || user.emailAddresses[0]?.emailAddress?.split('@')[0]}
                </span>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">
                    {user.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : 'User'}
                  </p>
                  <p className="text-xs leading-none text-muted-foreground">
                    {user.emailAddresses[0]?.emailAddress}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => navigate('/app/settings')}
                className="cursor-pointer"
              >
                <Settings className="mr-2 h-4 w-4" />
                <span>Settings</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <SignOutButton redirectUrl="/">
                <DropdownMenuItem className="cursor-pointer text-destructive focus:text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Sign Out</span>
                </DropdownMenuItem>
              </SignOutButton>
            </DropdownMenuContent>
          </DropdownMenu>
          ) : (
            /* Show sign in button for non-authenticated users */
            <Button 
              onClick={() => navigate('/auth/sign-in')}
              size="sm"
            >
              Sign In
            </Button>
          )}
        </div>
      </div>
      
      {/* Credit Purchase Modal */}
      {isSignedIn && (
        <CreditPurchaseModal
          open={showPurchaseModal}
          onOpenChange={setShowPurchaseModal}
          onPurchaseComplete={() => {
            setShowPurchaseModal(false);
            // The UsageStatusWidget will automatically refresh its data
          }}
        />
      )}
    </nav>
  );
};

export default CleanModernNavbar;